'use client';

import useSWR from 'swr';
import type { Locale } from '@/types/dictionary';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Logo from '@/components/Logo';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Link from '@/components/Link';
import getFooter from '@/actions/system/getFooter';
import Grid from '@mui/material/Grid';
import getTranslations from '@/utils/locale/getTranslations';
import Newsletter from './Newsletter';
import SocialNetworks from './SocialNetworks';
import PortableTextMarketingWebsite from '@/components/PortableText/PortableTextMarketingWebsite';
import NewsletterMarketingWebsite from './NewsletterMarketingWebsite';
import Links from './Links';
import RevealOnScroll from '@/components/animations/RevealOnScroll';
import getFooterLinkHref from '@/utils/getFooterLinkHref';
import useAuth from '@/lib/firebase/auth/store';

export default function Footer({
  locale,
  isMarketingWebsite = false,
  demoMode,
}: {
  locale: Locale;
  isMarketingWebsite?: boolean;
  demoMode?: boolean;
}) {
  // useAuth can't be run in an async component
  // useSWR allows us to fetch data in the component in one line
  const { data, error } = useSWR(['footer', locale], () => getFooter(locale));
  const t = getTranslations({ locale });
  const { userData } = useAuth();

  if (error) console.error('getFooter failed:', error);
  if (!data) return null;

  const customCompanyLogo = !!userData?.company?.logo && !demoMode;

  const content = (
    <AppBar position='static' variant='outlined' color='inherit' elevation={0} component={'footer'}>
      <Toolbar sx={{ py: 1.5 }}>
        <Grid container sx={{ width: '100%' }}>
          <Grid
            container
            size={8}
            columns={{ xs: 1, sm: 3, md: 3, lg: isMarketingWebsite ? 6 : 8 }}
          >
            <Grid size={1}>
              <Stack direction='column'>
                <Logo height={customCompanyLogo ? 50 : 30} demoMode={demoMode} />

                <PortableTextMarketingWebsite blocks={data?.sloganWithLink} footer />

                <Typography variant='body2' component='div' color='text.secondary'>
                  {t('footer.copyright', { year: `${new Date().getFullYear()}` })}
                </Typography>
              </Stack>
            </Grid>

            {[
              {
                title: t('footer.contact'),
                description: data?.contact,
              },
              {
                title: t('footer.location'),
                description: data?.location,
              },
            ].map(({ title, description }, index) => (
              <Links
                key={index}
                title={title}
                description={description}
                isMarketingWebsite={isMarketingWebsite}
                contactLink={data?.contactLink}
                googleMaps={data?.socialNetworks.googleMaps}
              />
            ))}

            {data?.links?.map(({ title, links }: any, index: number) => (
              <Grid key={index} size={1}>
                <Stack direction='column' spacing={0.75}>
                  <Typography
                    variant='body1'
                    sx={{
                      color: 'text.primary',
                      fontWeight: 'bold',
                    }}
                  >
                    {title}
                  </Typography>

                  <Stack direction='column' spacing={0.5}>
                    {links.map(({ text, href }: any, index: number) => (
                      <Link
                        variant='body2'
                        color='text.secondary'
                        href={getFooterLinkHref({ isMarketingWebsite, locale, href })}
                        key={index}
                      >
                        {text}
                      </Link>
                    ))}
                  </Stack>
                </Stack>
              </Grid>
            ))}

            {!isMarketingWebsite && (
              <Grid size={1}>
                {isMarketingWebsite ? <NewsletterMarketingWebsite /> : <Newsletter />}
              </Grid>
            )}

            <Grid size={1}>
              <SocialNetworks locale={locale} links={data?.socialNetworks} />
            </Grid>
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  );

  return isMarketingWebsite ? <RevealOnScroll>{content}</RevealOnScroll> : content;
}
